import React, {useState, useEffect} from 'react'
import {ToggleSection} from '../toggle-section'
import {useForm, Controller} from 'react-hook-form'
import {useNavigate} from 'react-router'
import {useAuth0} from '@auth0/auth0-react'
// import {yupResolver} from '@hookform/resolvers/yup'
import {Checkbox} from '@thryvlabs/maverick'
import {Input} from '../../accountManagement/shared/Input'
import {Select} from '../../accountManagement/shared/Select'
import {Button} from '../../accountManagement/shared/Button'
import Modal from '../../accountManagement/shared/Modal'
import {useAuthClient} from '../../utils/use-auth-client'
import {PillTagList, CompatiblePlansList} from './PillTagList'
import './coupon-select.css'

// import * as yup from 'yup'

const TODAY = new Date().toISOString().split('T')[0]

const FONT = "'Inter', 'Helvetica Neue', Arial, sans-serif"

const ORANGE_PRIMARY = {backgroundColor: '#FF5000', borderColor: '#FF5000'}
const ORANGE_SECONDARY = {
  borderColor: '#FF5000',
  color: '#FF5000',
}

const CB_INPUT = {width: '20px', height: '20px', minWidth: '20px'}
const CB_LABEL = {
  fontFamily: FONT,
  color: '#374151',
  margin: 0,
  fontSize: '14px',
  fontWeight: 500,
}

// const SCHEMA = yup.object().shape({
//   country: yup.string().min(4).required('Country is required'),
//   effectiveDate: yup.string().required('Effective date is required'),
//   endDate: yup.string().required('End date is required'),
//   couponCode: yup.string().required('Coupon code is required'),
//   couponDesc: yup.string().required('Coupon description is required'),
//   defaultFlag: yup.string().required('Default flag is required'),
//   newClientOnlyFlag: yup.string().required('New Client Only Flag is required'),
// })

const DEFAULT_VALUES = {
  country: '',
  couponCode: '',
  couponDesc: '',
  clientGroup: '',
  effectiveDate: '',
  endDate: '',
  defaultFlag: 'N',
  newClientOnlyFlag: 'N',
  compatiblePlans: [],
  compatibleRoles: [],
  incompatibleCoupons: [],
  exclusionDates: [],
}

const GridContent = ({title, children}) => (
  <div>
    {title && (
      <p
        style={{
          fontFamily: FONT,
          fontSize: '13px',
          fontWeight: 600,
          color: '#374151',
          margin: '0 0 8px',
        }}
      >
        {title}
      </p>
    )}
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '26px',
      }}
    >
      {children}
    </div>
  </div>
)

// eslint-disable-next-line no-unused-vars
const DynamicList = ({
  items,
  setItems,
  titlePrefix = 'Item',
  renderItem,
  canAdd = true,
  twoColumn = false,
}) => {
  const addItem = () => {
    setItems([...items, {}])
  }
  const removeItem = indexToRemove => {
    setItems(items.filter((_, index) => index !== indexToRemove))
  }

  if (twoColumn) {
    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '16px',
        }}
      >
        {items.map((item, index) => (
          <div key={index}>
            {renderItem(item, index)}
            <div style={{display: 'flex', gap: '8px', marginTop: '6px'}}>
              <Button
                variant="secondary"
                style={{
                  ...ORANGE_SECONDARY,
                  height: '26px',
                  padding: '0 12px',
                  fontSize: '12px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
                onClick={() => removeItem(index)}
              >
                Remove
              </Button>
              {index === items.length - 1 && (
                <Button
                  variant="primary"
                  buttonType="outlined"
                  style={{
                    ...ORANGE_PRIMARY,
                    opacity: canAdd ? 1 : 0.5,
                    fontSize: '12px',
                    height: '26px',
                    padding: '0 12px',
                  }}
                  onClick={addItem}
                  disabled={!canAdd}
                >
                  {`Add ${titlePrefix}`}
                </Button>
              )}
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <Button
            variant="primary"
            buttonType="outlined"
            style={{
              ...ORANGE_PRIMARY,
              fontSize: '13px',
              height: '34px',
              padding: '0 16px',
            }}
            onClick={addItem}
          >
            {`Add ${titlePrefix}`}
          </Button>
        )}
      </div>
    )
  }

  return (
    <>
      {items.map((item, index) => (
        <div key={index} style={{marginBottom: '10px'}}>
          <GridContent>{renderItem(item, index)}</GridContent>
          <Button
            variant="secondary"
            style={{
              ...ORANGE_SECONDARY,
              margin: '15px',
              marginLeft: '10px',
              width: '80px',
              height: '30px',
              padding: '0px',
              fontSize: '12px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
            onClick={() => removeItem(index)}
          >
            Remove
          </Button>
        </div>
      ))}
      <Button
        variant="primary"
        buttonType="outlined"
        style={{mt: '25px', ...ORANGE_PRIMARY, opacity: canAdd ? 1 : 0.5}}
        onClick={addItem}
        disabled={!canAdd}
      >
        {`Add ${titlePrefix}`}
      </Button>
    </>
  )
}

function CouponForm({submitForm}) {
  // const [compatiblePlans, setCompatiblePlans] = useState([{}])
  const [confirmAction, setConfirmAction] = useState(null)
  const [allRoles, setAllRoles] = useState([])
  const [availableCountries, setAvailableCountries] = useState([])
  // const [clientGroupOptions, setClientGroupOptions] = useState([])
  const navigate = useNavigate()
  const {user} = useAuth0()
  const client = useAuthClient()

  useEffect(() => {
    ;(async () => {
      try {
        const [rolesRes, countriesRes] = await Promise.all([
          client('coupons/roleNames'),
          client('coupons/countryList'),
          // client('coupons/clientGroup'),
        ])
        const rawRoles = rolesRes?.data?.[0]?.value || ''
        setAllRoles(
          rawRoles
            .split(',')
            .map(r => r.trim())
            .filter(Boolean),
        )
        const rawCountries = countriesRes?.data?.[0]?.value || ''
        setAvailableCountries(
          rawCountries
            .split(',')
            .map(entry => {
              const [value, ...rest] = entry.trim().split(':')
              return {label: rest.join(':').trim(), value: value.trim()}
            })
            .filter(c => c.value && c.label),
        )
        // const rawClientGroups = clientGroupRes?.data?.[0]?.value || ''
        // setClientGroupOptions(
        //   rawClientGroups
        //     .split(',')
        //     .map(g => g.trim())
        //     .filter(Boolean),
        // )
      } catch {
        // eslint-disable-next-line no-empty
      }
    })()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  const {
    formState: {isValid, isDirty, errors},
    handleSubmit,
    reset,
    // setValue,
    control,
    watch,
    getValues,
    trigger,
  } = useForm({
    defaultValues: DEFAULT_VALUES,
    mode: 'onTouched',
    reValidateMode: 'onChange',
    // resolver: yupResolver(SCHEMA),
  })

  const isSaveButtonDisabled = !isValid || !isDirty

  const defaultFlag = watch('defaultFlag') === 'Y'
  const newClientOnlyFlag = watch('newClientOnlyFlag') === 'Y'

  // React.useEffect(() => {
  //   // setSelectedCountry(AVAILABLE_COUNTRIES[0])
  // }, [isValid, isDirty])

  function onSubmit(formData) {
    const payload = {
      country: formData.country,
      couponCode: formData.couponCode,
      couponDesc: formData.couponDesc,
      effectiveDate: formData.effectiveDate,
      endDate: formData.endDate,
      // defaultFlag: formData.defaultFlag || 'N',
      // newClientOnlyFlag: formData.newClientOnlyFlag || 'N',
      createdBy: user?.email,
      modifiedBy: user?.email,

      clientGroup:
        formData.clientGroup && formData.clientGroup !== 'None'
          ? formData.clientGroup
          : null,
      compatiblePlans: (formData.compatiblePlans || [])
        .filter(plan => plan.planCode && plan.planCode.trim() !== '')
        .map(plan => ({
          planCode: plan.planCode,
          planDesc: plan.planDesc || '',
          // newAutoApply: plan.newAutoApply || 'N',
          // existingAutoApply: plan.existingAutoApply || 'N',
        })),
      compatibleRoles: (formData.compatibleRoles || [])
        .filter(role => role && role.roleName && role.roleName.trim() !== '')
        .map(role => role.roleName),
      exclusionDates: (formData.exclusionDates || [])
        .map(item => item.date)
        .filter(Boolean),
      incompatibleCoupons: (formData.incompatibleCoupons || [])
        .filter(
          coupon =>
            coupon &&
            coupon.incompatibleCoupon &&
            coupon.incompatibleCoupon.trim() !== '',
        )
        .map(coupon => coupon.incompatibleCoupon),
    }
    submitForm(payload)
  }

  return (
    <div className="coupon-form-fields">
      <ToggleSection title="Coupon Information" isOpen>
        <GridContent>
          <Controller
            control={control}
            name="country"
            rules={{required: 'Country is required'}}
            render={({field}) => (
              <Select
                label="Country"
                required
                options={availableCountries}
                errorText={errors?.country?.message}
                {...field}
              />
            )}
          />
          <div style={{display: 'flex', gap: '12px'}}>
            <div style={{flex: 1}}>
              <Controller
                control={control}
                name="effectiveDate"
                rules={{required: 'Effective date is required'}}
                render={({field}) => (
                  <Input
                    type="date"
                    label="Effective Date"
                    required
                    min={TODAY}
                    errorText={errors?.effectiveDate?.message}
                    value={field.value}
                    onChange={e => {
                      field.onChange(e.target.value)
                      if (getValues('endDate')) trigger('endDate')
                    }}
                    onBlur={field.onBlur}
                    name={field.name}
                  />
                )}
              />
            </div>
            <div style={{flex: 1}}>
              <Controller
                control={control}
                name="endDate"
                rules={{
                  required: 'End date is required',
                  validate: value => {
                    const eff = getValues('effectiveDate')
                    return (
                      !eff ||
                      !value ||
                      value > eff ||
                      'End date must be after Effective date'
                    )
                  },
                }}
                render={({field}) => (
                  <Input
                    type="date"
                    label="End Date"
                    required
                    min={TODAY}
                    errorText={errors?.endDate?.message}
                    value={field.value}
                    onChange={e => field.onChange(e.target.value)}
                    onBlur={field.onBlur}
                    name={field.name}
                  />
                )}
              />
            </div>
          </div>
        </GridContent>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: '26px',
            marginTop: '16px',
          }}
        >
          <Controller
            control={control}
            name="couponCode"
            rules={{
              required: 'Coupon code is required',
              maxLength: {
                value: 50,
                message: 'Coupon code must be at most 50 characters',
              },
            }}
            render={({field}) => (
              <Input
                type="text"
                label="Coupon Code"
                required
                errorText={errors?.couponCode?.message}
                value={field.value}
                onChange={e => field.onChange(e.target.value)}
                onBlur={field.onBlur}
                name={field.name}
              />
            )}
          />
          <Controller
            control={control}
            name="couponDesc"
            rules={{
              required: 'Coupon description is required',
              maxLength: {
                value: 250,
                message: 'Coupon description must be at most 250 characters',
              },
            }}
            render={({field}) => (
              <Input
                type="text"
                label="Coupon Description"
                required
                errorText={errors?.couponDesc?.message}
                value={field.value}
                onChange={e => field.onChange(e.target.value)}
                onBlur={field.onBlur}
                name={field.name}
              />
            )}
          />
          {/* <Controller
            control={control}
            name="clientGroup"
            rules={{required: 'Client Group is required'}}
            render={({field}) => (
              <Select
                label="Client Group"
                required
                options={clientGroupOptions}
                errorText={errors?.clientGroup?.message}
                {...field}
              />
            )}
          /> */}
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: '26px',
            marginTop: '20px',
          }}
        >
          <div style={{pointerEvents: 'none', opacity: 0.55}}>
            <Checkbox
              hasLabel
              style={CB_INPUT}
              name="defaultFlag"
              label={<label style={CB_LABEL}>Default Flag</label>}
              isChecked={defaultFlag}
              onChange={() => {}}
            />
          </div>
          <div style={{pointerEvents: 'none', opacity: 0.55}}>
            <Checkbox
              hasLabel
              style={CB_INPUT}
              name="newClientOnlyFlag"
              label={<label style={CB_LABEL}>New Client Only Flag</label>}
              isChecked={newClientOnlyFlag}
              onChange={() => {}}
            />
          </div>
        </div>
      </ToggleSection>

      <ToggleSection
        title="Required user roles"
        isOpen
        description="Coupon is accessible only to users with the below roles. If no roles are associated with the coupon, it is available to all users."
      >
        <PillTagList
          control={control}
          name="compatibleRoles"
          fieldKey="roleName"
          options={allRoles.map(r => ({label: r, value: r}))}
          placeholder="Select role…"
        />
      </ToggleSection>

      {defaultFlag && (
        <ToggleSection
          title="Compatible plans"
          isOpen
          description="Coupon is available only for the below plans"
        >
          <CompatiblePlansList control={control} name="compatiblePlans" />
        </ToggleSection>
      )}

      <ToggleSection
        title="Incompatible coupons"
        isOpen
        description="Coupon is incompatible with below list of coupons"
      >
        <PillTagList
          control={control}
          name="incompatibleCoupons"
          fieldKey="incompatibleCoupon"
          placeholder="Enter coupon code…"
        />
      </ToggleSection>

      <ToggleSection
        title="Exclusion dates"
        isOpen
        description="Coupon will not be available on the following dates"
      >
        <PillTagList
          control={control}
          name="exclusionDates"
          fieldKey="date"
          type="date"
          min={TODAY}
          placeholder="Pick a date…"
        />
      </ToggleSection>

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '10px',
        }}
      >
        <Button
          variant="secondary"
          style={ORANGE_SECONDARY}
          onClick={() => {
            if (isDirty) {
              setConfirmAction('cancel')
            } else {
              navigate('/coupons')
            }
          }}
        >
          Cancel
        </Button>
        <Button
          variant="secondary"
          style={ORANGE_SECONDARY}
          onClick={() => {
            if (isDirty) setConfirmAction('clear')
          }}
        >
          Clear
        </Button>
        <Button
          variant="primary"
          style={ORANGE_PRIMARY}
          disabled={isSaveButtonDisabled}
          onClick={handleSubmit(onSubmit)}
        >
          Save
        </Button>
      </div>
      <Modal
        isOpen={confirmAction !== null}
        onClose={() => setConfirmAction(null)}
        title={confirmAction === 'cancel' ? 'Cancel Changes' : 'Clear Changes'}
        size="md"
        footer={
          <>
            <Button
              variant="secondary"
              style={ORANGE_SECONDARY}
              onClick={() => setConfirmAction(null)}
            >
              No
            </Button>
            <Button
              variant="primary"
              style={ORANGE_PRIMARY}
              onClick={() => {
                if (confirmAction === 'cancel') {
                  navigate('/coupons')
                } else {
                  reset(DEFAULT_VALUES)
                  // setCompatiblePlans([{}])
                }
                setConfirmAction(null)
              }}
            >
              Yes
            </Button>
          </>
        }
        footerAlign="right"
      >
        <p
          style={{
            margin: 0,
            fontSize: '14px',
            color: '#4a4a4a',
            whiteSpace: 'nowrap',
          }}
        >
          {confirmAction === 'cancel'
            ? 'You have unsaved changes. Are you sure you want to leave?'
            : 'All entered data will be cleared. Do you want to proceed?'}
        </p>
      </Modal>
    </div>
  )
}

export {CouponForm}
